import React, { useEffect, useMemo, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { Dropdown, SelectPicker, Tooltip, Whisper } from 'rsuite'
import MenuIcon from '@rsuite/icons/Menu'
import PagePreviousIcon from '@rsuite/icons/PagePrevious'
import PageNextIcon from '@rsuite/icons/PageNext'
import ExitIcon from '@rsuite/icons/Exit'
import ShareOutlineIcon from '@rsuite/icons/ShareOutline'
import navGroups, { activeNavItemId } from 'config/MenuItems'
import { useGetClientQuery } from 'pages/client/clientApiSlice'
import useAuth from 'hooks/Auth'
import { ROUTES } from 'routers/Routes'
import logoUrl from 'assets/images/xlrs-horz.svg'
import './appShell.scss'

const COLLAPSED_KEY = 'nav.collapsed'
const DOCUMENT_AI_CONSOLE_URL =
  'https://console.cloud.google.com/ai/document-ai/locations/us/processors/2b72ddee935b3fb0/v2/overview?project=xlrs-prod'

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true'
  } catch {
    return false
  }
}

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'U'

const currentUserName = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') ?? 'null')
    return (user?.displayName as string) || (user?.email as string) || 'User'
  } catch {
    return 'User'
  }
}

/**
 * Signed-in layout, after FinAIQ: grouped side navigation that collapses to an
 * icon rail (remembered) and becomes a drawer on small screens, and a slim top
 * bar with the client switcher and user menu. Pages get the full width beside
 * the navigation.
 */
const AppShell: React.FC = () => {
  const { pathname } = useLocation()
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { selectedClientId, setSelectedClientId } = useAuth()

  const { data: clients } = useGetClientQuery({
    page: { size: 200, number: 0 }
  })
  const clientOptions = useMemo(
    () =>
      (clients?.content ?? [])
        .filter((client) => client.id != null)
        .map((client) => ({
          label: client.name ?? `Client ${client.id}`,
          value: String(client.id)
        })),
    [clients]
  )

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, String(collapsed))
    } catch {
      // Preference only; the shell works without storage.
    }
  }, [collapsed])

  // A navigation closes the mobile drawer.
  useEffect(() => setDrawerOpen(false), [pathname])

  const activeId = activeNavItemId(pathname)
  const userName = currentUserName()

  return (
    <div
      className={classNames('shell', {
        'shell--collapsed': collapsed,
        'shell--drawer-open': drawerOpen
      })}
    >
      <aside className="shell__nav" aria-label="Main navigation">
        <Link to={ROUTES.DASHBOARD} className="shell__brand" aria-label="Home">
          <img src={logoUrl} alt="Exceleris" />
        </Link>

        <nav className="shell__menu">
          {navGroups.map((group) => (
            <div key={group.id} className="shell__group">
              <span className="shell__group-label">{group.label}</span>
              <ul>
                {group.items.map(({ id, name, link, icon: Icon }) => {
                  const item = (
                    <NavLink
                      to={link}
                      className={classNames('shell__item', {
                        'shell__item--active': activeId === id
                      })}
                      aria-current={activeId === id ? 'page' : undefined}
                    >
                      <Icon className="shell__item-icon" />
                      <span className="shell__item-label">{name}</span>
                    </NavLink>
                  )
                  return (
                    <li key={id}>
                      {collapsed ? (
                        <Whisper
                          placement="right"
                          trigger="hover"
                          speaker={<Tooltip>{name}</Tooltip>}
                        >
                          {item}
                        </Whisper>
                      ) : (
                        item
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </nav>

        <button
          type="button"
          className="shell__collapse"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          {collapsed ? <PageNextIcon /> : <PagePreviousIcon />}
          <span className="shell__item-label">Collapse</span>
        </button>
      </aside>

      <button
        type="button"
        className="shell__scrim"
        aria-label="Close navigation"
        tabIndex={drawerOpen ? 0 : -1}
        onClick={() => setDrawerOpen(false)}
      />

      <div className="shell__main">
        <header className="shell__topbar">
          <button
            type="button"
            className="shell__burger"
            aria-label="Open navigation"
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </button>

          <div className="shell__client">
            <span className="shell__client-label">Client</span>
            <SelectPicker
              data={clientOptions}
              placeholder="Select a client"
              value={selectedClientId}
              onChange={(value) => setSelectedClientId(value)}
              cleanable
              searchable={clientOptions.length > 8}
              size="sm"
              style={{ width: 240 }}
              aria-label="Client"
            />
          </div>

          <div className="shell__topbar-actions">
            <a
              className="shell__link"
              href={DOCUMENT_AI_CONSOLE_URL}
              target="_blank"
              rel="noreferrer"
            >
              Document AI console <ShareOutlineIcon />
            </a>
            <Dropdown
              placement="bottomEnd"
              renderToggle={(props, ref) => (
                <button
                  {...props}
                  ref={ref}
                  type="button"
                  className="shell__user"
                  aria-label={`Account menu for ${userName}`}
                >
                  <span className="shell__avatar" aria-hidden>
                    {initialsOf(userName)}
                  </span>
                  <span className="shell__user-name">{userName}</span>
                </button>
              )}
            >
              <Dropdown.Item as={Link} to={ROUTES.LOGOUT} icon={<ExitIcon />}>
                Sign out
              </Dropdown.Item>
            </Dropdown>
          </div>
        </header>

        <main className="shell__content">
          <Outlet />
          <footer className="shell__footer">
            © {new Date().getFullYear()} Exceleris. All rights reserved.
          </footer>
        </main>
      </div>
    </div>
  )
}

export default AppShell
